import { useState, useMemo } from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { CapacitySnapshot } from '../types/capacity.types';
import { ScenarioInputs } from '../types/scenario.types';
import ScenarioControls from '../components/ScenarioControls';
import ScenarioResults from '../components/ScenarioResults';
import { getUniqueRoles } from '../utils/capacityMetrics';
import { runScenarioModel, getDefaultScenarioInputs } from '../scenario/fteCalculator';

interface ScenarioModelerViewProps {
  snapshot: CapacitySnapshot;
}

export default function ScenarioModelerView({ snapshot }: ScenarioModelerViewProps) {
  const availableRoles = useMemo(() => getUniqueRoles(snapshot), [snapshot]);

  // Initialize with default inputs that include all available roles
  const [inputs, setInputs] = useState<ScenarioInputs>(() => {
    const defaults = getDefaultScenarioInputs();
    // Set default visits per patient for all available roles
    const visitsPerPatient: { [role: string]: number } = {};
    availableRoles.forEach((role) => {
      visitsPerPatient[role] = defaults.visitsPerPatient[role] || 2; // Default 2 visits per patient
    });
    return { ...defaults, visitsPerPatient };
  });

  // Calculate scenario results
  const results = useMemo(() => {
    return runScenarioModel(inputs, snapshot);
  }, [inputs, snapshot]);

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Scenario Modeler
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Strategic staffing planning based on utilization targets and projected volume
      </Typography>

      <Grid container spacing={3}>
        {/* Left Column: Controls */}
        <Grid item xs={12} md={4}>
          <ScenarioControls
            inputs={inputs}
            onChange={setInputs}
            availableRoles={availableRoles}
          />
        </Grid>

        {/* Right Column: Results */}
        <Grid item xs={12} md={8}>
          <ScenarioResults results={results} />
        </Grid>
      </Grid>
    </Box>
  );
}
